import React, { FC } from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSidebarStore } from '../../store/sidebarStore';

interface BasicNavbarProps {
    item: any,
    handleSubMenuClick: (_href: string) => void,
}

const BasicNavbar: FC<BasicNavbarProps> = (props) => {
  const location = useLocation();
  const isSidebarOpen = useSidebarStore((state) => state.isSidebarOpen);
  const setIsSidebarOpen = useSidebarStore((state) => state.setIsSidebarOpen);
  const { t } = useTranslation();

  const isActive = location.pathname.startsWith(props.item.href);
  const isHighlighted = !!props.item.highlight && !isActive;

  const toggleSidebarOpen = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleClick = () => {
    if (isActive) {
      toggleSidebarOpen();
      props.handleSubMenuClick('');
      return;
    }
    setIsSidebarOpen(false);
    props.handleSubMenuClick(props.item.href);
  };

  if (isHighlighted) {
    return (
      <NavLink
        onClick={handleClick}
        to={props.item.href}
        className="block"
        data-cy={`nav${props.item.href}`}
      >
        <div
          className={`
            my-3 flex cursor-pointer items-center rounded-xl
            border-2 border-setup py-3
            animate-setup-pulse
            bg-[rgba(255,45,85,0.06)]
            transition-colors duration-200 hover:bg-[rgba(255,45,85,0.12)]
            ${isSidebarOpen ? 'mx-3 gap-3 px-4' : 'mx-2 justify-center px-0'}
          `}
        >
          <img
            src={props.item.iconPrimary}
            alt=""
            className="h-4 w-4 flex-shrink-0 object-contain"
          />
          {isSidebarOpen && (
            <span className="text-sm font-semibold tracking-wide text-setup">
              {t(props.item.label)}
            </span>
          )}
        </div>
      </NavLink>
    );
  }

  return (
    <NavLink
      onClick={handleClick}
      to={props.item.href}
      className="block"
      data-cy={`nav${props.item.href}`}
    >
      <ListItem
        key={props.item.label}
        disablePadding
        sx={{ display: 'block' }}
      >
        <ListItemButton
          sx={{
            minHeight: 44,
            mx: 1,
            borderRadius: '8px',
            justifyContent: isSidebarOpen ? 'initial' : 'center',
            pl: isSidebarOpen ? '14px' : 0,
            pr: isSidebarOpen ? '14px' : 0,
            transition: 'background-color 0.15s ease',
            backgroundColor: isActive ? '#0C485E' : 'transparent',
            color: isActive ? 'white' : '#374151',
            '&:hover': {
              backgroundColor: isActive ? '#0d5270' : 'rgba(12,72,94,0.05)',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isSidebarOpen ? '14px' : 0,
              justifyContent: 'center',
            }}
          >
            <img
              src={isActive ? props.item.iconSecondary : props.item.iconPrimary}
              alt=""
              className={`h-4 w-4 object-contain ${isActive ? '' : 'opacity-70'}`}
            />
          </ListItemIcon>
          {isSidebarOpen && (
            <p className={`text-sm ${isActive ? 'font-medium' : 'font-normal opacity-80'}`}>
              {t(props.item.label)}
            </p>
          )}
        </ListItemButton>
      </ListItem>
    </NavLink>
  );
};

export default BasicNavbar;
